import React from 'react';
import { Plus, Warning } from '@phosphor-icons/react';
import { LOCATION_LABELS } from '../helpers';

/**
 * Compact dashboard strip — replaces the old KPI tiles + Team Progress +
 * Deliveries at Risk block (≈460px) with two lines (~110px), per the
 * Vehicle Flow design handoff. The vehicle list is the protagonist now.
 */

// Order matters: the busiest yards read first.
const STRIP_LOCS = ['melville', 'garling', 'south', 'offsite', 'transit'];
const TEAM_LABELS = { workshop: 'Workshop', tint: 'Tint', detail: 'Detail', paint: 'Paint' };

export default function DashStrip({ stats, canAdd, onAdd, onSearchRisk }) {
  const byLoc = stats?.by_location || {};
  // Zero-count yards are noise — drop them unless everything is zero.
  const locs = STRIP_LOCS.filter((l) => (byLoc[l] ?? 0) > 0);
  const teams = stats?.team_progress || [];
  const risks = stats?.at_risk || [];
  const blocked = stats?.blocked ?? 0;
  const ready = stats?.ready ?? 0;

  return (
    <div className="dstrip">
      {/* Row 1 — headline count, yards, state pills, Add Vehicle */}
      <div className="dstrip-r1">
        <div className="dstrip-total">
          <span className="dstrip-n num">{stats?.total ?? 0}</span>
          <span className="dstrip-cap">ACTIVE</span>
        </div>

        {locs.length > 0 && (
          <div className="dstrip-locs">
            {locs.map((l) => (
              <div className="dstrip-loc" key={l}>
                <span className={`dstrip-dot ${l}`} />
                <span className="num dstrip-locn">{byLoc[l]}</span>
                <span className="dstrip-locl">{LOCATION_LABELS[l]}</span>
              </div>
            ))}
          </div>
        )}

        <div className="dstrip-div" />

        <div className="dstrip-pills">
          <Pill n={blocked} label="blocked" tone={blocked > 0 ? 'red' : 'grey'} />
          <Pill n={stats?.today_count ?? 0} label="today's del." tone={(stats?.today_count ?? 0) > 0 ? 'red' : 'grey'} />
          <Pill n={ready} label="ready" tone={ready > 0 ? 'green' : 'grey'} />
        </div>

        <div style={{ flex: 1 }} />

        {canAdd && (
          <button className="dstrip-add" onClick={onAdd}>
            <Plus size={13} weight="bold" />Add Vehicle
          </button>
        )}
      </div>

      {/* Row 2 — stage mini-bars + at-risk ticker */}
      <div className="dstrip-r2">
        <div className="dstrip-stages">
          {teams.map((t) => {
            const { total = 0, done = 0, wip = 0 } = t;
            const pctDone = total ? (done / total) * 100 : 0;
            const pctWip  = total ? (wip / total) * 100 : 0;
            const cls = total && done === total ? 'full' : done > 0 ? 'some' : 'none';
            return (
              <div className="dstage" key={t.team}>
                <div className="dstage-top">
                  <span className="dstage-lbl">{TEAM_LABELS[t.team] || t.team}</span>
                  <span className={`dstage-n num ${cls}`}>{done}/{total}</span>
                </div>
                <div className="dstage-track">
                  <div className="dstage-done" style={{ width: `${pctDone}%` }} />
                  <div className="dstage-wip"  style={{ width: `${pctWip}%` }} />
                </div>
              </div>
            );
          })}
        </div>

        <div className="dstrip-div sm" />

        <span className="dstrip-risk-cap">
          <Warning size={11} weight="fill" style={{ verticalAlign: '-1px', marginRight: 3 }} />
          AT RISK · {risks.length}
        </span>

        <div className="dstrip-ticker">
          {risks.slice(0, 4).map((r) => (
            <span className="rchip" key={r.id} title={`${r.model} · ${r.stock_number}`}>
              <span className="num rchip-t">{r.delivery_time || '—'}</span>
              <span className="rchip-m">{r.model}</span>
              <span className="num rchip-s">{r.stock_number}</span>
            </span>
          ))}
          {risks.length > 4 && <span className="rchip-more">+{risks.length - 4}</span>}
          {risks.length === 0 && <span className="rchip-none">Nothing at risk today.</span>}
        </div>

        {risks.length > 0 && (
          <button className="dstrip-viewall" onClick={onSearchRisk}>View all →</button>
        )}
      </div>
    </div>
  );
}

function Pill({ n, label, tone }) {
  return (
    <span className={`dpill ${tone}`}>
      <span className="num dpill-n">{n}</span> {label}
    </span>
  );
}
